import { useEffect, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { useAuth } from "../contexts/AuthContext";
import { adminService, UserSignupData, GetRecentUsersResponse } from "../services/adminService";
import { Link } from "react-router-dom";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { ResponsiveLine } from "@nivo/line";
import { UserData } from "../types/user";

const DashboardPage: React.FC = () => {

    const { userData } = useAuth();
    const [userCount, setUserCount] = useState<number | null>(null);
    const [coachCount, setCoachCount] = useState<number | null>(null);
    const [athleteCount, setAthleteCount] = useState<number | null>(null);
    const [signupTrend, setSignupTrend] = useState<UserSignupData[]>([]);
    const [weeklyTrend, setWeeklyTrend] = useState<UserSignupData[]>([]);
    const [trendLoading, setTrendLoading] = useState(true);
    const [weeklyTrendLoading, setWeeklyTrendLoading] = useState(true);
    const [trendView, setTrendView] = useState<'12-week' | '7-day'>('12-week');
    const [recentUsers, setRecentUsers] = useState<UserData[] | null>(null);
    const [recentUsersLoading, setRecentUsersLoading] = useState(true);

    useEffect(() => {
        document.title = 'Dashboard - Peak^ Admin';

        const fetchUserCount = async () => {
            try {
                const count = await adminService.getUserCount();
                setUserCount(count);
            } catch (error) {
                console.error('Error fetching user count:', error);
            }
        };

        const fetchCoachCount = async () => {
            try {
                const count = await adminService.getCoachCount();
                setCoachCount(count);
            } catch (error) {
                console.error('Error fetching coach count:', error);
            }
        };

        const fetchSignupTrend = async () => {
            try {
                setTrendLoading(true);
                const trend = await adminService.get3MonthNewUserTrend();
                setSignupTrend(trend);
            } catch (error) {
                console.error('Error fetching signup trend:', error);
            } finally {
                setTrendLoading(false);
            }
        };

        const fetchWeeklyTrend = async () => {
            try {
                setWeeklyTrendLoading(true);
                const trend = await adminService.get7DayNewUserTrend();
                setWeeklyTrend(trend);
            } catch (error) {
                console.error('Error fetching weekly trend:', error);
            } finally {
                setWeeklyTrendLoading(false);
            }
        };

        const fetchMostRecentUsers = async () => {
            try {
                setRecentUsersLoading(true);
                const recent = await adminService.getMostRecentUsers();
                setRecentUsers(recent);
            } catch (error) {
                console.error('Error fetching recent users:', error);
            } finally {
                setRecentUsersLoading(false);
            }
        }

        fetchUserCount();
        fetchCoachCount();
        fetchSignupTrend();
        fetchWeeklyTrend();
        fetchMostRecentUsers();

        if (userCount !== null && coachCount !== null) {
            setAthleteCount(userCount - coachCount);
        }
    }, [userCount, coachCount]);

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 md:h-[calc(100vh-64px)] p-4 md:p-6 overflow-hidden">
                <h1 className="text-xl md:text-2xl font-heading shrink-0">Welcome back, {userData?.firstName}</h1>

                {/* Stats Row */}
                <div className="w-full grid grid-cols-3 gap-2 md:gap-4 shrink-0">
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-6 gap-2 md:gap-6 items-start justify-between border">
                        <div className="text-xl md:text-3xl font-bold">{userCount?.toLocaleString() ?? '—'}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total users</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-6 gap-2 md:gap-6 items-start justify-between border">
                        <div className="w-full flex items-start justify-between">
                            <div className="text-xl md:text-3xl font-bold">{athleteCount?.toLocaleString() ?? '—'}</div>
                            <Link to="/users" className="hover:text-gray-400 hidden sm:block">
                                <ArrowUpRightIcon className="w-4 h-4 inline-block" />
                            </Link>
                        </div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total athletes</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-6 gap-2 md:gap-6 items-start justify-between border">
                        <div className="w-full flex items-start justify-between">
                            <div className="text-xl md:text-3xl font-bold">{coachCount?.toLocaleString() ?? '—'}</div>
                            <Link to="/coaches" className="hover:text-gray-400 hidden sm:block">
                                <ArrowUpRightIcon className="w-4 h-4 inline-block" />
                            </Link>
                        </div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total coaches</div>
                    </div>
                </div>

                {/* Chart and Recent Users */}
                <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1 min-h-0">
                    {/* Chart */}
                    <div className="lg:col-span-2 w-full bg-white rounded-xl flex flex-col p-4 md:p-6 gap-4 md:gap-6 border min-h-[360px] md:min-h-0">
                        <div className="flex items-center justify-between shrink-0">
                            <div className="font-heading text-base md:text-lg font-medium">Signup Trend</div>
                            <div className="flex items-center bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setTrendView('7-day')}
                                    className={`px-3 py-1 text-xs md:text-sm rounded-md transition-colors ${
                                        trendView === '7-day'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    7 Days
                                </button>
                                <button
                                    onClick={() => setTrendView('12-week')}
                                    className={`px-3 py-1 text-xs md:text-sm rounded-md transition-colors ${
                                        trendView === '12-week'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    12 Weeks
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 min-h-[200px] md:min-h-0 w-full">
                            {(trendView === '12-week' ? trendLoading : weeklyTrendLoading) ? (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    Loading...
                                </div>
                            ) : (trendView === '12-week' ? signupTrend : weeklyTrend).length > 0 ? (
                                <ResponsiveLine
                                    data={[
                                        {
                                            id: "Signups",
                                            data: trendView === '12-week' ? signupTrend : weeklyTrend
                                        }
                                    ]}
                                    margin={{ top: 20, right: 20, bottom: 20, left: 40 }}
                                    xScale={{ type: "point" }}
                                    yScale={{
                                        type: "linear",
                                        min: 0,
                                        max: "auto",
                                        stacked: false,
                                        reverse: false
                                    }}
                                    axisTop={null}
                                    axisRight={null}
                                    axisBottom={null}
                                    axisLeft={{
                                        tickSize: 5,
                                        tickPadding: 5,
                                        tickRotation: 0,
                                        legendOffset: -40,
                                        legendPosition: "middle",
                                        tickValues: 5
                                    }}
                                    colors={["#3b82f6"]}
                                    curve="cardinal"
                                    gridYValues={5}
                                    pointSize={6}
                                    pointColor={{ theme: "background" }}
                                    pointBorderWidth={2}
                                    pointBorderColor={{ from: "serieColor" }}
                                    enableArea={true}
                                    areaOpacity={0.1}
                                    useMesh={true}
                                    enableGridX={false}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    No signup data available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Users */}
                    <div className="w-full bg-white rounded-xl flex flex-col p-4 md:p-6 gap-3 md:gap-4 border min-h-0 overflow-hidden">
                        <div className="font-heading text-base md:text-lg font-medium shrink-0">10 Most Recent Signups</div>
                        <div className="flex-1 flex flex-col overflow-y-auto min-h-0">
                            {recentUsersLoading ? (
                                <div className="w-full h-32 flex items-center justify-center text-gray-400">
                                    Loading...
                                </div>
                            ) : recentUsers && recentUsers.length > 0 ? (
                                recentUsers.map(user => (
                                    <Link to={`/users?user=${user.uid}`} key={user.email} className="w-full flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg">
                                        {user.imageUrl ? (
                                            <img
                                                src={user.imageUrl || '/default-profile.png'}
                                                alt={user.name || 'User'}
                                                className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover shrink-0"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary text-xs md:text-sm font-grotesk flex items-center justify-center text-white shrink-0">
                                                {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                            </div>
                                        )}
                                        <div className="flex flex-col min-w-0">
                                            <div className="font-medium capitalize text-sm truncate">{user.name || 'New User'}</div>
                                            <div className="text-xs text-gray-500 truncate">{user.email || 'No email available'}</div>
                                        </div>
                                    </Link>
                                ))
                            ) : (
                                <div className="w-full h-32 flex items-center justify-center text-gray-400">
                                    No recent users found
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardWrapper>
    );
};

export default DashboardPage;
